import React, { Fragment, useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import PropTypes from 'prop-types';

import { requestInsert, requestUpdate } from 'api/standard/code';
import Select from 'components/common-sfims/form/Select';
import Modal from 'components/common-sfims/modal/Common';
import { array2ValueAndText } from 'function/convert';
import { onEnterKeyPress, onInputChange } from 'function/event';
import { getText } from 'function/language';
import { getErrorMessage } from 'function/message';
import { isValidRequired } from 'function/valid';
import { useConfirmModalState } from 'global/confirmModalState';
import useObjReducer from 'hooks/useObjReducer';
import { RESPONSE_CODE } from 'types/const';
import { RETURN_YN_SELECT } from 'types/type';
import comma from 'utils/format/comma';
import format from 'utils/string/format';

/**
 * 기준 정보 > 속성 코드 관리 - 등록/수정
 */
const Setting = ({ show, inform, order, onHide, onSetting }) => {
  /* recoil */
  const { setConfirmModal } = useConfirmModalState();

  /* state */
  // 코드 등록/수정 요청 데이터
  const [codeData, setCodeData] = useObjReducer({});
  // 설정 비활성화 여부
  const [isDisableSetting, setIsDisableSetting] = useState(false);

  /* watch */
  useEffect(() => {
    // 모달이 표시된 경우 데이터 초기화
    if (!show) {
      return;
    }

    let codeData = {
      codeCd: '',
      codeNmKr: '',
      useYn: ''
    };

    if (inform.seq) {
      codeData = {
        codeNmKr: inform.codeNmKr,
        useYn: inform.useYn
      };
    }

    setCodeData(codeData);
  }, [show]);

  /* event */
  // 설정
  const onSubmit = async () => {
    if (isDisableSetting) {
      return;
    }

    setIsDisableSetting(true);

    try {
      // 필수 항목 입력 유효성 확인
      if (!isValidRequired(codeData)) {
        throw {
          code: RESPONSE_CODE[
            inform.seq ? 'BadRequestUpdate' : 'BadRequestInsert'
          ]
        };
      }

      if (inform.seq) {
        const { code, message } = await requestUpdate(inform.seq, codeData);

        if (code !== RESPONSE_CODE.Success) {
          throw {
            code,
            error: message
          };
        }
      } else {
        const { code, message } = await requestInsert({
          ...inform,
          ...codeData
        });

        if (code !== RESPONSE_CODE.Success) {
          throw {
            code,
            error: message
          };
        }
      }

      // 설정 시 이벤트 실행
      onSetting();

      // 모달 숨김
      onHide();
    } catch (error) {
      setConfirmModal({
        isOpen: true,
        message: getErrorMessage(error?.code, error?.error)
      });
    } finally {
      setIsDisableSetting(false);
    }
  };

  /* view */
  return (
    <Modal
      show={show}
      className={{ footer: 'align-items-center justify-content-center' }}
      onHide={onHide}
    >
      <Fragment key="header">
        {format(getText(inform.seq ? 'updateCode' : 'insertCode'), {
          order: comma(order)
        })}
      </Fragment>
      <Fragment key="body">
        <Form.Group className="mb-3">
          <Form.Label>
            {!inform.seq && '* '}
            {getText('code')}
          </Form.Label>
          <Form.Control
            readOnly={Boolean(inform.seq)}
            size="sm"
            type="text"
            value={(inform.seq ? inform : codeData).codeCd}
            autoFocus={!inform.seq}
            maxLength={20}
            name="codeCd"
            placeholder={getText('codeCdPlaceholder')}
            onChange={event => {
              if (!inform.seq) {
                onInputChange(event, setCodeData);
              }
            }}
            onKeyUp={event => onEnterKeyPress(event, onSubmit)}
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>* {getText('codeNmKr')}</Form.Label>
          <Form.Control
            size="sm"
            type="text"
            value={codeData.codeNmKr}
            autoFocus={Boolean(inform.seq)}
            maxLength={20}
            name="codeNmKr"
            placeholder={getText('codeNmKrPlaceholder')}
            onChange={event => onInputChange(event, setCodeData)}
            onKeyUp={event => onEnterKeyPress(event, onSubmit)}
          />
        </Form.Group>
        <Form.Group>
          <Form.Label>* {getText('useYn')}</Form.Label>
          <Select
            datas={array2ValueAndText(RETURN_YN_SELECT, 'key', 'value')}
            placeholder="useYn"
            value={codeData.useYn}
            className="w-100"
            name="useYn"
            onChange={event => onInputChange(event, setCodeData)}
          />
        </Form.Group>
      </Fragment>
      <Fragment key="footer">
        <Button variant="secondary" onClick={onHide}>
          {getText('cancel')}
        </Button>
        <Button disabled={isDisableSetting} className="ms-2" onClick={onSubmit}>
          {getText('ok')}
        </Button>
      </Fragment>
    </Modal>
  );
};

Setting.propTypes = {
  // 표시 여부
  show: PropTypes.bool,

  // 코드 정보
  inform: PropTypes.object,

  // 차수
  order: PropTypes.number,

  // 모달을 숨긴 경우 이벤트
  onHide: PropTypes.func,

  // 설정 시 이벤트
  onSetting: PropTypes.func
};
Setting.defaultProps = {
  show: false,
  inform: {},
  order: 0
};

export default Setting;
